import { TooltipIconButton } from "@components/Atoms/TooltipIconButton";
import type { DiffTreeItem } from "@ext/git/core/Diff/components/Changes/diffTreeItems";
import type { DiffFlattenTreeAnyItem } from "@ext/git/core/GitDiffItemCreator/RevisionDiffPresenter";
import t from "@ext/localization/locale/translate";
import { FileStatus } from "@ext/Watchers/model/FileStatus";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@ui-kit/Tooltip";
import {
	TreeActions,
	TreeCheckbox,
	TreeIcon,
	TreeIndicator,
	TreeIndicatorBar,
	TreeMeta,
	TreeRow,
	TreeTitle,
	TreeTrailing,
} from "@ui-kit/Tree";
import { PUBLISH_TREE_TITLE_OFFSET } from "../constants";
import { PublishChangeCount } from "./PublishChangeCount";

const STATUS_LABELS = {
	[FileStatus.new]: "diff.type.added",
	[FileStatus.delete]: "diff.type.deleted",
	[FileStatus.modified]: "diff.type.modified",
	[FileStatus.rename]: "diff.type.breadcrumb",
} as const;

export type PublishTreeRowProps = {
	item: DiffTreeItem;
	depth: number;
	isActive: boolean;
	canDiscard: boolean;
	isSelected: (entry: DiffFlattenTreeAnyItem) => boolean;
	onSelect: (entry: DiffFlattenTreeAnyItem, checked: boolean) => void;
	onOpen: (entry: DiffFlattenTreeAnyItem) => void;
	onDiscard: (entry: DiffFlattenTreeAnyItem) => void;
};

export const PublishTreeRow = ({
	item,
	depth,
	isActive,
	canDiscard,
	isSelected,
	onSelect,
	onOpen,
	onDiscard,
}: PublishTreeRowProps) => {
	const isGroup = item.variant === "group";
	const overview = item.entry.type === "node" ? null : item.entry.overview;
	const statusLabel = overview && STATUS_LABELS[overview.status];

	return (
		<TreeRow
			depth={depth}
			isActive={isActive}
			isGroup={isGroup}
			isSelected={isGroup ? undefined : isSelected(item.entry)}
			item={item}
			onClick={isGroup ? undefined : () => onOpen(item.entry)}
			onSelect={isGroup ? undefined : (checked) => onSelect(item.entry, checked)}
			titleOffset={PUBLISH_TREE_TITLE_OFFSET}
		>
			{item.accentColor && statusLabel && (
				<TreeIndicator>
					<TooltipProvider>
						<Tooltip>
							<TooltipTrigger asChild>
								<TreeIndicatorBar color={item.accentColor} />
							</TooltipTrigger>
							<TooltipContent side="right">{t(statusLabel)}</TooltipContent>
						</Tooltip>
					</TooltipProvider>
				</TreeIndicator>
			)}
			<TreeCheckbox />
			<TreeIcon icon={item.icon} />
			<TreeTitle>{item.title}</TreeTitle>
			{!isGroup && (
				<TreeTrailing>
					{overview && (
						<TreeMeta>
							<PublishChangeCount added={overview.added} deleted={overview.removed} />
						</TreeMeta>
					)}
					{canDiscard && (
						<TreeActions>
							<TooltipIconButton
								className="shrink-0 h-auto p-0"
								icon="reply"
								onClick={(event) => {
									event.stopPropagation();
									onDiscard(item.entry);
								}}
								size="sm"
								tooltip={t("git.discard.selected-file-arrow-tooltip")}
								variant="text"
							/>
						</TreeActions>
					)}
				</TreeTrailing>
			)}
		</TreeRow>
	);
};
